import React from 'react';
import { Navigate } from 'react-router-dom';
import { PrivateRoute } from 'shared/components/PrivateRoute';
import type { RequiredRole } from 'shared/auth/roles';
import { useAuth } from 'shared/contexts/AuthContext';
import { paths, routeSegments } from 'shared/routing/paths';
import { AdminLayout } from 'features/admin/components/AdminLayout';
import { defineRoute, AppRoute } from './types';
import { lazyNamed } from './lazy';

const AdminSectionPlaceholderPage = lazyNamed(
  () => import('features/admin/pages/AdminSectionPlaceholderPage'),
  'AdminSectionPlaceholderPage',
);
const AdminExpertsPage = lazyNamed(() => import('features/admin/pages/AdminExpertsPage'), 'AdminExpertsPage');
const AdminCampaignsPage = lazyNamed(() => import('features/admin/pages/AdminCampaignsPage'), 'AdminCampaignsPage');
const AdminCampaignListPage = lazyNamed(
  () => import('features/admin/pages/AdminCampaignListPage'),
  'AdminCampaignListPage',
);
const AdminCampaignDetailPage = lazyNamed(
  () => import('features/admin/pages/AdminCampaignDetailPage'),
  'AdminCampaignDetailPage',
);
const AdminCampaignDefaultsPage = lazyNamed(
  () => import('features/admin/pages/AdminCampaignDefaultsPage'),
  'AdminCampaignDefaultsPage',
);
const AdminCampaignBuilderPage = lazyNamed(
  () => import('features/admin/pages/AdminCampaignBuilderPage'),
  'AdminCampaignBuilderPage',
);
const AdminExpertsSearchPage = lazyNamed(
  () => import('features/admin/pages/AdminExpertsSearchPage'),
  'AdminExpertsSearchPage',
);
const AdminReviewersPage = lazyNamed(() => import('features/admin/pages/AdminReviewersPage'), 'AdminReviewersPage');
const AdminReviewerDetailPage = lazyNamed(
  () => import('features/admin/pages/AdminReviewerDetailPage'),
  'AdminReviewerDetailPage',
);
const AdminReviewerTeamPage = lazyNamed(
  () => import('features/admin/pages/AdminReviewerTeamPage'),
  'AdminReviewerTeamPage',
);
const AdminExpertPoolsPage = lazyNamed(
  () => import('features/admin/pages/AdminExpertPoolsPage'),
  'AdminExpertPoolsPage',
);
const AdminExternalPlatformsPage = lazyNamed(
  () => import('features/admin/pages/AdminExternalPlatformsPage'),
  'AdminExternalPlatformsPage',
);
const AdminCustomersPage = lazyNamed(() => import('features/admin/pages/AdminCustomersPage'), 'AdminCustomersPage');
const AdminOpportunitiesPage = lazyNamed(
  () => import('features/admin/pages/AdminOpportunitiesPage'),
  'AdminOpportunitiesPage',
);
const AdminSyntheticDataPage = lazyNamed(
  () => import('features/admin/pages/AdminSyntheticDataPage'),
  'AdminSyntheticDataPage',
);
const AdminSyntheticDataDashboardPage = lazyNamed(
  () => import('features/admin/pages/AdminSyntheticDataDashboardPage'),
  'AdminSyntheticDataDashboardPage',
);
const AdminSyntheticDataSeedsPage = lazyNamed(
  () => import('features/admin/pages/AdminSyntheticDataSeedsPage'),
  'AdminSyntheticDataSeedsPage',
);
const AdminSyntheticDataSeedReviewPage = lazyNamed(
  () => import('features/admin/pages/AdminSyntheticDataSeedReviewPage'),
  'AdminSyntheticDataSeedReviewPage',
);
const AdminSyntheticDataSeedPage = lazyNamed(
  () => import('features/admin/pages/AdminSyntheticDataSeedPage'),
  'AdminSyntheticDataSeedPage',
);
const AdminMessageBoardPage = lazyNamed(
  () => import('features/admin/pages/AdminMessageBoardPage'),
  'AdminMessageBoardPage',
);
const AdminEmailsPage = lazyNamed(() => import('features/admin/pages/AdminEmailsPage'), 'AdminEmailsPage');
const AdminEmailUsersPage = lazyNamed(() => import('features/admin/pages/AdminEmailUsersPage'), 'AdminEmailUsersPage');
const AdminEmailTemplatesPage = lazyNamed(
  () => import('features/admin/pages/AdminEmailTemplatesPage'),
  'AdminEmailTemplatesPage',
);
const AdminProjectsJobsPage = lazyNamed(
  () => import('features/admin/pages/AdminProjectsJobsPage'),
  'AdminProjectsJobsPage',
);
const AdminJobInstructionsPage = lazyNamed(
  () => import('features/admin/pages/AdminJobInstructionsPage'),
  'AdminJobInstructionsPage',
);
const AdminMediaGalleryPage = lazyNamed(
  () => import('features/admin/pages/AdminMediaGalleryPage'),
  'AdminMediaGalleryPage',
);
const AdminAssignJobPage = lazyNamed(() => import('features/admin/pages/AdminAssignJobPage'), 'AdminAssignJobPage');
const AdminReviewsPage = lazyNamed(() => import('features/admin/pages/AdminReviewsPage'), 'AdminReviewsPage');
const AdminAssignTeamPoolsPage = lazyNamed(
  () => import('features/admin/pages/AdminAssignTeamPoolsPage'),
  'AdminAssignTeamPoolsPage',
);
const AdminOpenAiFeatherPage = lazyNamed(
  () => import('features/admin/pages/AdminOpenAiFeatherPage'),
  'AdminOpenAiFeatherPage',
);
const AdminBillingPage = lazyNamed(() => import('features/admin/pages/AdminBillingPage'), 'AdminBillingPage');
const AdminPayPeriodsPage = lazyNamed(() => import('features/admin/pages/AdminPayPeriodsPage'), 'AdminPayPeriodsPage');
const AdminEarningsPage = lazyNamed(() => import('features/admin/pages/AdminEarningsPage'), 'AdminEarningsPage');
const AdminBonusPage = lazyNamed(() => import('features/admin/pages/AdminBonusPage'), 'AdminBonusPage');
const AdminClientToolPaymentsPage = lazyNamed(
  () => import('features/admin/pages/AdminClientToolPaymentsPage/index'),
  'AdminClientToolPaymentsPage',
);
const AdminInvoicesPage = lazyNamed(() => import('features/admin/pages/AdminInvoicesPage'), 'AdminInvoicesPage');
const AdminFinanceReportingPage = lazyNamed(
  () => import('features/admin/pages/AdminFinanceReportingPage/index'),
  'AdminFinanceReportingPage',
);
const AdminFinanceExportsPage = lazyNamed(
  () => import('features/admin/pages/AdminFinanceExportsPage'),
  'AdminFinanceExportsPage',
);
const AdminPayrollPage = lazyNamed(() => import('features/admin/pages/AdminPayrollPage'), 'AdminPayrollPage');
const AdminOtherPage = lazyNamed(() => import('features/admin/pages/AdminOtherPage'), 'AdminOtherPage');
const AdminCustomerSupportBotPage = lazyNamed(
  () => import('features/admin/pages/AdminCustomerSupportBotPage'),
  'AdminCustomerSupportBotPage',
);

const AdminPermissionsPage = lazyNamed(() => import('features/admin/pages/AdminPermissionsPage'), 'AdminPermissionsPage');
const AdminTeamPermissionsPage = lazyNamed(() => import('features/admin/pages/AdminTeamPermissionsPage'), 'AdminTeamPermissionsPage');

const adminRootId = 'admin.shell';

function AdminIndexRedirect() {
  const { isAdmin, isContractorAdmin } = useAuth();
  return <Navigate to={isContractorAdmin && !isAdmin ? paths.admin.expertsSearch : paths.admin.experts} replace />;
}

function adminChild(
  id: string,
  path: string,
  element: React.ReactNode,
  layout: 'shell' | 'full-width' = 'shell',
  requireRole: RequiredRole = 'fullAdmin',
): AppRoute {
  return defineRoute(
    {
      id,
      path,
      area: 'admin',
      guard: 'inherited',
      layout,
      kind: 'page',
      parentId: adminRootId,
    },
    {
      path: path.slice(paths.admin.root.length + 1),
      element: <PrivateRoute requireRole={requireRole}>{element}</PrivateRoute>,
    },
  );
}

const adminRootRoute = defineRoute(
  {
    id: adminRootId,
    path: paths.admin.root,
    area: 'admin',
    guard: 'admin',
    layout: 'shell',
    kind: 'layout',
  },
  {
    path: paths.admin.root,
    element: (
      <PrivateRoute requireRole="admin">
        <AdminLayout />
      </PrivateRoute>
    ),
    children: [
      defineRoute(
        {
          id: 'admin.index',
          path: paths.admin.root,
          area: 'admin',
          guard: 'inherited',
          layout: 'shell',
          kind: 'redirect',
          parentId: adminRootId,
          redirectTo: paths.admin.experts,
        },
        { index: true, element: <AdminIndexRedirect /> },
      ),
      adminChild('admin.permissions', paths.admin.permissions, <AdminPermissionsPage />),
      adminChild('admin.teamPermissions', paths.admin.teamPermissions, <AdminTeamPermissionsPage />, 'full-width'),
      adminChild('admin.experts', paths.admin.experts, <AdminExpertsPage />),
      adminChild('admin.expertsSearch', paths.admin.expertsSearch, <AdminExpertsSearchPage />, 'full-width', 'admin'),
      adminChild('admin.expertPools', paths.admin.expertPools, <AdminExpertPoolsPage />, 'full-width'),
      adminChild('admin.reviewers', paths.admin.reviewers, <AdminReviewersPage />, 'full-width'),
      adminChild(
        'admin.reviewerDetail',
        paths.admin.root + '/' + routeSegments.admin.reviewerDetail,
        <AdminReviewerDetailPage />,
        'full-width',
      ),
      adminChild(
        'admin.reviewerTeam',
        paths.admin.root + '/' + routeSegments.admin.reviewerTeam,
        <AdminReviewerTeamPage />,
        'full-width',
      ),
      adminChild(
        'admin.externalPlatforms',
        paths.admin.externalPlatforms,
        <AdminExternalPlatformsPage />,
        'full-width',
      ),
      adminChild('admin.metaSrt', paths.admin.metaSrt, <AdminSectionPlaceholderPage title="Meta SRT" />, 'full-width'),
      adminChild(
        'admin.openAiFeatherExperts',
        paths.admin.openAiFeatherExperts,
        <AdminSectionPlaceholderPage title="OpenAI Feather" />,
        'full-width',
      ),
      adminChild(
        'admin.cloudflareDestinations',
        paths.admin.cloudflareDestinations,
        <AdminSectionPlaceholderPage title="Cloudflare Destinations" />,
        'full-width',
      ),
      adminChild('admin.campaigns', paths.admin.campaigns, <AdminCampaignsPage />),
      adminChild('admin.campaignList', paths.admin.campaignList, <AdminCampaignListPage />, 'full-width'),
      adminChild('admin.campaignDefaults', paths.admin.campaignDefaults, <AdminCampaignDefaultsPage />, 'full-width'),
      adminChild('admin.createCampaign', paths.admin.createCampaign, <AdminCampaignBuilderPage />, 'full-width'),
      adminChild(
        'admin.campaignDetail',
        paths.admin.root + '/' + routeSegments.admin.campaign,
        <AdminCampaignDetailPage />,
        'full-width',
      ),
      adminChild('admin.projectsJobs', paths.admin.projectsJobs, <AdminProjectsJobsPage />),
      adminChild('admin.assignExperts', paths.admin.assignExperts, <AdminAssignJobPage />, 'full-width'),
      adminChild('admin.assignJob', paths.admin.assignJob, <AdminAssignJobPage />, 'full-width'),
      adminChild('admin.jobInstructions', paths.admin.jobInstructions, <AdminJobInstructionsPage />, 'full-width'),
      adminChild('admin.mediaGallery', paths.admin.mediaGallery, <AdminMediaGalleryPage />, 'full-width'),
      adminChild('admin.customers', paths.admin.customers, <AdminCustomersPage />, 'full-width'),
      adminChild(
        'admin.customer',
        paths.admin.root + '/' + routeSegments.admin.customer,
        <AdminCustomersPage />,
        'full-width',
      ),
      adminChild('admin.reviews', paths.admin.reviews, <AdminReviewsPage />),
      adminChild('admin.reviewTeamPools', paths.admin.reviewTeamPools, <AdminAssignTeamPoolsPage />, 'full-width'),
      adminChild(
        'admin.reviewOpenAiFeather',
        paths.admin.reviewOpenAiFeather,
        <AdminOpenAiFeatherPage />,
        'full-width',
      ),
      adminChild('admin.emails', paths.admin.emails, <AdminEmailsPage />),
      adminChild('admin.emailUsers', paths.admin.emailUsers, <AdminEmailUsersPage />, 'full-width'),
      adminChild('admin.emailTemplates', paths.admin.emailTemplates, <AdminEmailTemplatesPage />, 'full-width'),
      adminChild('admin.billing', paths.admin.billing, <AdminBillingPage />),
      adminChild('admin.payPeriods', paths.admin.payPeriods, <AdminPayPeriodsPage />, 'full-width'),
      adminChild('admin.payroll', paths.admin.payroll, <AdminPayrollPage />, 'full-width'),
      adminChild('admin.earningsPending', paths.admin.earningsPending, <AdminPayrollPage />, 'full-width'),
      adminChild('admin.paymentsPending', paths.admin.paymentsPending, <AdminPayrollPage />, 'full-width'),
      adminChild('admin.earnings', paths.admin.earnings, <AdminEarningsPage />, 'full-width'),
      adminChild('admin.bonuses', paths.admin.bonuses, <AdminBonusPage />, 'full-width'),
      adminChild(
        'admin.clientToolPayments',
        paths.admin.clientToolPayments,
        <AdminClientToolPaymentsPage />,
        'full-width',
      ),
      adminChild(
        'admin.paymentTokens',
        paths.admin.paymentTokens,
        <AdminSectionPlaceholderPage title="Payment Tokens" />,
        'full-width',
      ),
      adminChild('admin.invoices', paths.admin.invoices, <AdminInvoicesPage />, 'full-width'),
      adminChild('admin.financeReporting', paths.admin.financeReporting, <AdminFinanceReportingPage />, 'full-width', 'finance'),
      adminChild('admin.financeExports', paths.admin.financeExports, <AdminFinanceExportsPage />, 'full-width', 'finance'),
      adminChild('admin.messageBoard', paths.admin.messageBoard, <AdminMessageBoardPage />, 'full-width'),
      adminChild('admin.opportunities', paths.admin.opportunities, <AdminOpportunitiesPage />, 'full-width', 'admin'),
      adminChild('admin.syntheticData', paths.admin.syntheticData, <AdminSyntheticDataPage />),
      adminChild(
        'admin.syntheticDataDashboard',
        paths.admin.syntheticDataDashboard,
        <AdminSyntheticDataDashboardPage />,
        'full-width',
      ),
      adminChild('admin.syntheticDataSeeds', paths.admin.syntheticDataSeeds, <AdminSyntheticDataSeedsPage />, 'full-width'),
      adminChild(
        'admin.syntheticDataSeedReview',
        paths.admin.root + '/' + routeSegments.admin.syntheticDataSeedReview,
        <AdminSyntheticDataSeedReviewPage />,
        'full-width',
      ),
      adminChild(
        'admin.syntheticDataSeed',
        paths.admin.root + '/' + routeSegments.admin.syntheticDataSeed,
        <AdminSyntheticDataSeedPage />,
        'full-width',
      ),
      adminChild('admin.other', paths.admin.other, <AdminOtherPage />),
      adminChild(
        'admin.customerServiceBot',
        paths.admin.customerServiceBot,
        <AdminCustomerSupportBotPage />,
        'full-width',
      ),
    ],
  },
);

export const ADMIN_ROUTES: AppRoute[] = [adminRootRoute];
